import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'
import { Button } from '../ui/button'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, resetError: () => void) => ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  eventId: string | null
}

/**
 * Error Boundary component to catch React errors and display a fallback UI
 *
 * @example
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <ErrorBoundary fallback={(error, reset) => (
 *   <div>
 *     <p>Something went wrong: {error.message}</p>
 *     <button onClick={reset}>Try again</button>
 *   </div>
 * )}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /** Removes the afterSendEvent listener registered for the captured event. */
  private unsubscribeAfterSend?: () => void

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      eventId: null,
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      eventId: null,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error Boundary caught an error:', error, errorInfo)

    // Capture error in Sentry with component stack context
    const eventId = Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    })

    // captureException returns an id even when the event is subsequently dropped
    // by ignoreErrors/beforeSend (auth noise, extension errors, dev builds with
    // no DSN). Showing that id handed users an "Error ID" support could never
    // look up. Only surface it once the client confirms the event actually went
    // out — afterSendEvent fires only for transmitted events.
    const client = Sentry.getClient()
    this.unsubscribeAfterSend = client?.on('afterSendEvent', (sentEvent) => {
      if (sentEvent.event_id !== eventId) return
      this.setState({ eventId })
      this.unsubscribeAfterSend?.()
      this.unsubscribeAfterSend = undefined
    })

    this.props.onError?.(error, errorInfo)
  }

  componentWillUnmount(): void {
    this.unsubscribeAfterSend?.()
    this.unsubscribeAfterSend = undefined
  }

  resetError = () => {
    this.unsubscribeAfterSend?.()
    this.unsubscribeAfterSend = undefined
    this.setState({
      hasError: false,
      error: null,
      eventId: null,
    })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.resetError)
      }

      return (
        <DefaultErrorFallback
          error={this.state.error}
          resetError={this.resetError}
          eventId={this.state.eventId}
        />
      )
    }

    return this.props.children
  }
}

interface DefaultErrorFallbackProps {
  error: Error
  resetError: () => void
  eventId: string | null
}

/**
 * Default error fallback UI - displays an AlertDialog instructing users to share
 * error details with the Cortado Pro support team in Slack
 */
function DefaultErrorFallback({ error, resetError, eventId }: DefaultErrorFallbackProps) {
  const copyErrorDetails = async () => {
    const errorReport = [
      'Cortado Error Report',
      '--------------------',
      `Error ID: ${eventId || 'N/A'}`,
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      `Error: ${error.message}`,
    ].join('\n')

    try {
      await navigator.clipboard.writeText(errorReport)
      toast.success('Error details copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <AlertDialog open={true}>
      <AlertDialogContent
        onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}
        className="sm:max-w-md"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">
            Something went wrong
          </AlertDialogTitle>
          <AlertDialogDescription>
            An unexpected error occurred. Please share this error with the support
            team in Cortado Pro's Slack.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {eventId && (
          <div className="rounded-md border border-muted bg-muted/50 p-3">
            <p className="text-xs font-medium text-muted-foreground">Error ID</p>
            <p className="mt-1 text-sm font-mono text-foreground select-all break-all">
              {eventId}
            </p>
          </div>
        )}

        <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            onClick={copyErrorDetails}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy Error Details
          </Button>
          <Button
            onClick={resetError}
            variant="outline"
            className="w-full sm:w-auto"
          >
            Try Again
          </Button>
          <Button
            onClick={() => window.location.reload()}
            variant="default"
            className="w-full sm:w-auto"
          >
            Reload Page
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * Simpler error boundary for smaller components
 *
 * @example
 * <SimpleErrorBoundary>
 *   <SmallComponent />
 * </SimpleErrorBoundary>
 */
export function SimpleErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm">
          <p className="font-medium text-destructive">Error loading component</p>
          <p className="mt-1 text-muted-foreground">{error.message}</p>
          <Button
            onClick={reset}
            variant="outline"
            size="sm"
            className="mt-3"
          >
            Try Again
          </Button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  )
}
